import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import WarningPage from './WarningPage';
import RetryNotice, { RetryInline } from './RetryNotice';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import VersionsList from './VersionsList';
import AppliesLine from './AppliesLine';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective, splitVersions } from './effectiveWindow';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';

const METRIC_KEY = 'new_lead_assignment_count';

const OPERATORS = [
  { value: '<', label: 'is below' },
  { value: '<=', label: 'is at or below' },
  { value: '=', label: 'equals' },
  { value: '>=', label: 'is at or above' },
  { value: '>', label: 'is above' },
];

// Single Global model, same screen shape as SfrConfig.jsx. The stored
// number is a MONTHLY count - the dashboard prorates it to whatever period
// is actually selected (see new_lead_assignment_count_dashboard.controller.js).
const NewLeadAssignmentCountConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screens
  const [existing, setExisting] = useState(null);
  const [versions, setVersions] = useState([]); // every version that still matters (see VersionsList)
  const [editTarget, setEditTarget] = useState(null); // the version an Edit replaces
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ comparison_operator: '<', threshold_value: '', is_active: true, ...emptyEffective() });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // a blank threshold only complains after the first Save attempt
  const [submitted, setSubmitted] = useState(false);

  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen (fast clicks must not show an older answer)
  const fetchCurrent = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "no threshold configured".
    return apiRequest({ url: `warning-dashboard/global-thresholds/${METRIC_KEY}?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load the threshold');
          return;
        }
        const parts = splitVersions(res?.data?.rows || [], month);
        setVersions(parts.sorted);
        setExisting(parts.current || parts.open); // what applies this month
        setEditTarget(parts.open || parts.current); // what an edit replaces
        setError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setError(err?.response?.data?.message || 'Could not load the threshold');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setLoading(false);
      });
  };

  useEffect(() => {
    fetchCurrent();
  }, [month]); // reload when the working month changes

  const openEdit = () => {
    setForm(
      existing
        ? { comparison_operator: existing.comparison_operator, threshold_value: existing.threshold_value, is_active: true, ...emptyEffective() }
        : { comparison_operator: '<', threshold_value: '', is_active: true, ...emptyEffective() }
    );
    setSaveError(null);
    setSubmitted(false);
    setEditing(true);
  };

  // BUG-03: never blank, never negative (the server enforces it too)
  const thresholdField = numberField(form.threshold_value, 'Threshold', RULES.nonNegative, submitted, { helper: false });

  const handleSave = () => {
    setSubmitted(true);
    if (validateNumber(form.threshold_value, 'Threshold', RULES.nonNegative)) return;
    setSaving(true);
    const request = editTarget
      ? apiRequest({ url: `warning-dashboard/global-thresholds/${METRIC_KEY}/${editTarget.id}`, method: 'put', data: form })
      : apiRequest({ url: `warning-dashboard/global-thresholds/${METRIC_KEY}`, method: 'post', data: form });

    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save this threshold');
          return;
        }
        setEditing(false);
        return fetchCurrent();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save this threshold'))
      .finally(() => setSaving(false));
  };

  const [toggling, setToggling] = useState(false); // the switch request is still in flight
  const handleToggleActive = () => {
    if (!existing) return;
    if (toggling) return;
    setToggling(true);
    const nextActive = !existing.is_active;
    setExisting((prev) => ({ ...prev, is_active: nextActive }));
    apiRequest({ url: `warning-dashboard/global-thresholds/${METRIC_KEY}/${existing.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') {
          setExisting((prev) => ({ ...prev, is_active: !nextActive }));
        } else {
        fetchCurrent({ silent: true }); // a toggle can start a new version from this month
      }
      })
      .catch(() => {
        setExisting((prev) => ({ ...prev, is_active: !nextActive }));
      })
      .finally(() => setToggling(false));
  };

  const operatorLabel = (op) => OPERATORS.find((o) => o.value === op)?.label || op;

  return (
    <WarningPage
      title="New Lead Assignment Count - Threshold Configuration"
      actions={
        <Button component={Link} to={`/dashboard/warning-dashboard/new-lead-assignment-count?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
          View Results
        </Button>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Box sx={{ mb: 3 }}>
        <MetricDefinitionNote metricKey={METRIC_KEY} />
      </Box>

      {loading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading threshold...</Typography>
        </Box>
      )}

      {!loading && error && <RetryNotice message={error} onRetry={fetchCurrent} />}

      {!loading && !error && !editing && (
        <Box display="flex" alignItems="center" gap={2}>
          {existing ? (
            <>
              <Typography variant="h6">
                Warning when leads assigned {operatorLabel(existing.comparison_operator)} {existing.threshold_value} per month
              </Typography>
              <Switch checked={!!existing.is_active} disabled={toggling} onChange={handleToggleActive} />
              <Typography variant="body2" color="text.secondary">{existing.is_active ? 'Active' : 'Inactive'}</Typography>
              <UpdateButton size="small" action={openEdit} />
            </>
          ) : (
            <>
              <Typography color="text.secondary">No threshold configured yet.</Typography>
              <Button variant="contained" onClick={openEdit}>Set Threshold</Button>
            </>
          )}
        </Box>
      )}

      {!loading && !error && !editing && <AppliesLine row={existing} month={month} />}

      {!loading && !error && !editing && (
        <VersionsList rows={versions} month={month} renderValue={(r) => `${r.comparison_operator} ${r.threshold_value}`} />
      )}

      {!loading && !error && editing && (
        <Box display="flex" flexDirection="column" gap={2} sx={{ maxWidth: 460 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>Warning when leads assigned</Typography>
            <TextField
              select
              size="small"
              value={form.comparison_operator}
              onChange={(e) => setForm({ ...form, comparison_operator: e.target.value })}
              sx={{ minWidth: 160 }}
            >
              {OPERATORS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              value={form.threshold_value}
              onChange={(e) => setForm({ ...form, threshold_value: e.target.value })}
              sx={{ width: 120 }}
              {...thresholdField.props}
            />
            <Typography>per month</Typography>
          </Box>

          <EffectiveFromField form={form} setForm={setForm} />


          <ActiveSwitch form={form} setForm={setForm} offHint="While off, no warning is raised for this metric." />

          {thresholdField.error && <Typography color="error.main" variant="body2">{thresholdField.error}</Typography>}
          {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

          <Box display="flex" gap={1}>
            <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
            <Button variant="text" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          </Box>

          {existing && (
            <Chip
              size="small"
              variant="outlined"
              label="Saving adds a new version from the month you choose - earlier months keep what they had"
            />
          )}
        </Box>
      )}
    </WarningPage>
  );
};

export default NewLeadAssignmentCountConfig;
